// RiepilogoView.jsx — amm-v3 (A3, ondata 30): la vista "Riepilogo annuale"
// (pagina 'riepilogo') in React, flag-gated (shell_amm_v3_riepilogo,
// default OFF).
//
// Replica 1:1 renderRiepilogo di admin-module.js r.2245-2307: stesso titolo,
// stesso sottotitolo (con la variante print-only 'stampato il …', A5-039),
// stessi bottoni ('Perché non torna? ›' SOLO a residuo non nullo, 'Stampa',
// 'Esporta tutto (ZIP)', select anno MOBILE [anno+1, anno, anno-1]), stessi
// 3 KPI, stessa sezione 'Per gruppo' (barre % pagato) e stessa distribuzione
// mensile (barre previsto/pagato sovrapposte).
//
// STAMPA (R5 della spike): la vista usa le CLASSI del v1 (amm-no-print /
// amm-print-only) e window.print(); il beforeprint globale di admin-module
// legge AMM.currentPage === 'riepilogo' — il wrap del motore lo tiene
// aggiornato, quindi anche Ctrl+P diretto stampa correttamente.
//
// DELEGHE: openQuadraturaSpiegata (modale condiviso col Piano imposte), eur,
// toast, export ZIP (stesso path api del v1). NIENTE interceptor: censimento
// nel contract test (unico re-render interno: il select anno, solo mondo v1).
//
// STATO (parità di persistenza): l'anno vive su AMM.riepAnno (default anno
// corrente, r.748) — flip di flag senza perdere l'anno scelto. COMMIT
// ATOMICO come il v1 (la pagina cambia solo a fetch concluso).
//
// DEVIAZIONI DICHIARATE:
//  D-A3.3 doppio click su 'Esporta tutto (ZIP)' = 1 richiesta con etichetta
//         'Attendere…' (parità col busy() v1, che QUI c'è già: replicata).
//  D-A3.2 ricarica fallita con dati presenti ⇒ banner + Riprova (famiglia
//         D-A1.5/D-A2.2); al primo giro errore pieno con Riprova.

import React from 'react';
import { AM_NEW_TEXTS, MESI } from './api.js';
import {
  AR_TEXTS, AR_GRP, AR_COLORS,
  riepAnnoStato, maxMese, pctPagato, barHeights, annoOptions, gruppoLabel,
  subTesto, emptyMessage, readRiepilogo, esportaZip, apriQuadratura,
} from './riepilogo-api.js';

function StatoBox({ error, onRetry }) {
  if (error) {
    const msg = error && error.message ? error.message : String(error);
    return (
      <div role="alert" className="bg-white border rounded-lg p-6 text-center" style={{ borderColor: '#fecaca', background: '#fef2f2' }}>
        <div style={{ color: '#b91c1c', fontSize: 13, fontWeight: 600 }}>{'⚠ ' + msg}</div>
        <button
          type="button"
          style={{ marginTop: 12, background: '#0e7490', color: '#fff', border: 0, borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}
          onClick={onRetry}
        >{AM_NEW_TEXTS.riprova}</button>
      </div>
    );
  }
  return <div className="text-slate-400 text-sm">Caricamento…</div>;
}

export function RiepilogoView({ win, tick }) {
  const A = win.AMM;
  const [pendingAnno, setPendingAnnoState] = React.useState(() => riepAnnoStato(A));
  const setPendingAnno = (anno) => {
    A.riepAnno = anno; // write-through (r.2289: self.riepAnno = Number(...))
    setPendingAnnoState(anno);
  };
  const [dati, setDati] = React.useState({ loading: true, error: null, vista: null });
  const [reloadN, setReloadN] = React.useState(0);
  const busyRef = React.useRef(false); // guardia SINCRONA anti doppio-click (D-A3.3)
  const [busyZip, setBusyZip] = React.useState(false);

  React.useEffect(() => {
    let vivo = true;
    const anno = pendingAnno;
    setDati((d) => ({ ...d, loading: true, error: null }));
    readRiepilogo(win, anno).then(
      (r) => { if (vivo) setDati({ loading: false, error: null, vista: { anno, ...r } }); },
      (e) => { if (vivo) setDati((d) => ({ ...d, loading: false, error: e })); }
    );
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win, tick, reloadN, pendingAnno]);

  const vista = dati.vista;
  const anno = vista ? vista.anno : pendingAnno;
  const t = vista ? vista.totali : { previsto: 0, pagato: 0, residuo: 0 };
  const max = vista ? maxMese(vista.perMese) : 1;

  const onExportZip = async () => {
    if (busyRef.current) return;
    busyRef.current = true; setBusyZip(true);
    try {
      try { await esportaZip(win, anno); }
      catch (e) { A.toast(AR_TEXTS.exportZipFallito + (e && e.message ? e.message : e)); } // verbatim r.2305
    } finally {
      busyRef.current = false; setBusyZip(false);
    }
  };

  const kpis = [
    { label: AR_TEXTS.kpiPrevisto, val: A.eur(t.previsto), color: AR_COLORS.previsto },
    { label: AR_TEXTS.kpiPagato, val: A.eur(t.pagato), color: AR_COLORS.pagato },
    { label: AR_TEXTS.kpiResiduo, val: A.eur(t.residuo), color: AR_COLORS.residuo },
  ];

  return (
    <div data-amm-v3="riepilogo">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold">{AR_TEXTS.title}</h1>
          <div className="text-xs text-slate-400 amm-no-print">{subTesto(anno)}</div>
          {/* variante print-only (r.2254): accesa dal CSS di stampa del v1 */}
          <div className="text-xs text-slate-400 amm-print-only" style={{ display: 'none' }}>
            {subTesto(anno) + AR_TEXTS.stampatoIl + new Date().toLocaleDateString('it-IT')}
          </div>
        </div>
        <div className="flex items-center gap-2 amm-no-print">
          {!!t.residuo && (
            <button
              id="amm-riep-perche"
              type="button"
              className="bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 text-xs px-3 py-2 rounded font-medium"
              onClick={() => apriQuadratura(win, anno)}
            >{AR_TEXTS.perche}</button>
          )}
          <button id="amm-riep-stampa" type="button" className="bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs px-3 py-2 rounded font-medium" onClick={() => win.print()}>{AR_TEXTS.stampa}</button>
          <button id="amm-export-zip" type="button" disabled={busyZip} className="bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs px-3 py-2 rounded font-medium" onClick={onExportZip}>
            {busyZip ? AM_NEW_TEXTS.attendere : AR_TEXTS.exportZip}
          </button>
          <select
            id="amm-anno"
            className="border rounded px-2 py-1.5 text-sm"
            value={anno}
            onChange={(ev) => setPendingAnno(Number(ev.target.value))}
          >
            {annoOptions(anno).map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>

      {/* D-A3.2: ricarica fallita con dati presenti ⇒ banner + Riprova */}
      {dati.error && vista && (
        <div role="alert" className="mb-3 px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-between gap-3" style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>
          <span>{AM_NEW_TEXTS.erroreRicarica + (dati.error.message || String(dati.error))}</span>
          <button
            type="button"
            style={{ background: '#0e7490', color: '#fff', border: 0, borderRadius: 8, padding: '6px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}
            onClick={() => setReloadN((n) => n + 1)}
          >{AM_NEW_TEXTS.riprova}</button>
        </div>
      )}

      {!vista ? (
        <StatoBox error={dati.error} onRetry={() => setReloadN((n) => n + 1)} />
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-5">
            {kpis.map((k) => (
              <div key={k.label} className="bg-white border rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">{k.label}</div>
                <div className="text-xl font-semibold mt-1" style={{ color: k.color }}>{k.val}</div>
              </div>
            ))}
          </div>

          <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2 ml-1">{AR_TEXTS.perGruppo}</div>
          <div className="bg-white border rounded-lg overflow-hidden">
            {vista.perGruppo.length ? vista.perGruppo.map((g) => {
              const pct = pctPagato(g);
              return (
                <div key={g.gruppo} className="flex items-center gap-3 px-4 py-3 border-t first:border-t-0 text-sm">
                  <div className="flex-1 font-medium">{gruppoLabel(g)}</div>
                  <div style={{ width: 110, textAlign: 'right' }}>{A.eur(g.previsto)}</div>
                  <div style={{ width: 110, textAlign: 'right', color: AR_COLORS.pagato }}>{A.eur(g.pagato)}</div>
                  <div style={{ width: 110, textAlign: 'right', color: AR_COLORS.residuo }}>{A.eur(g.previsto - g.pagato)}</div>
                  <div style={{ width: 120 }}>
                    <div style={{ height: 7, background: AR_COLORS.barBg, borderRadius: 9999, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: pct + '%', background: AR_COLORS.residuo }} />
                    </div>
                    <div className="text-[10px] text-slate-400 mt-1">{pct + AR_TEXTS.pagatoPct}</div>
                  </div>
                </div>
              );
            }) : (
              <div className="p-8 text-center text-slate-500 text-sm">{emptyMessage(anno)}</div>
            )}
          </div>

          <div className="text-[11px] uppercase tracking-wider text-slate-400 mt-6 mb-2 ml-1">{AR_TEXTS.distribuzione}</div>
          <div className="bg-white border rounded-lg p-3">
            <div className="grid grid-cols-12 gap-2 items-end" style={{ height: 130 }}>
              {vista.perMese.map((mm) => {
                const h = barHeights(mm, max);
                return (
                  <div key={mm.mese} className="flex flex-col items-center justify-end h-full gap-1">
                    <div style={{ position: 'relative', width: '60%', height: '100%', display: 'flex', alignItems: 'flex-end' }}>
                      <div style={{ height: h.hP + '%', width: '100%', background: AR_COLORS.barPrevisto, borderRadius: '4px 4px 0 0' }} />
                      <div style={{ height: h.hPaid + '%', width: '100%', background: AR_COLORS.barPagato, borderRadius: '4px 4px 0 0', position: 'absolute', bottom: 0, left: 0 }} />
                    </div>
                    <div className="text-[9px] uppercase text-slate-400">{MESI[mm.mese - 1]}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Factory (pattern createAmmV3Cruscotto)
export function createAmmV3Riepilogo(win) {
  let root = null;
  let mountedEl = null;
  let tick = 0;

  function render() {
    const container = win.document.getElementById('amm-page');
    if (!container) throw new Error('#amm-page assente');
    const manomesso = root && mountedEl === container && tick > 0
      && !container.querySelector('[data-amm-v3]');
    if (!root || mountedEl !== container || !container.isConnected || manomesso) {
      if (root) { try { root.unmount(); } catch (e) { /* root già ferito/smontato */ } }
      container.replaceChildren();
      root = win.ReactDOM.createRoot(container);
      mountedEl = container;
    }
    tick += 1;
    root.render(<RiepilogoView win={win} tick={tick} />);
  }

  function unmount() {
    if (root) {
      try { root.unmount(); } catch (e) { /* già smontato */ }
      root = null;
    }
    mountedEl = null;
  }

  function isMounted() { return !!root; }

  return { render, unmount, isMounted };
}
